import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormComponentRefs
 */

/**
 * Manages contact form state visibility (form/error/success).
 * Handles editor block selection to preview different form states.
 *
 * @extends {Component<ContactFormComponentRefs>}
 */
class ContactFormComponent extends Component {
  /** @type {Map<HTMLElement, boolean>} */
  #initialStates = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#applyFormState();
    this.#saveInitialStates();
  }

  #applyFormState() {
    const formState = this.dataset.formState;

    if (formState === 'success') {
      this.#showOnly('success');
    } else if (formState === 'error') {
      this.#showSteps(['error', 'form']);
      this.#populateErrorMessage();
    }
  }

  #saveInitialStates() {
    for (const block of this.querySelectorAll('[data-contact-step]')) {
      this.#initialStates.set(/** @type {HTMLElement} */ (block), block.hidden);
    }
  }

  /**
   * @param {string} step
   */
  #showOnly(step) {
    for (const block of this.querySelectorAll('[data-contact-step]')) {
      /** @type {HTMLElement} */ (block).hidden = block.dataset.contactStep !== step;
    }
  }

  /**
   * @param {string[]} steps
   */
  #showSteps(steps) {
    for (const block of this.querySelectorAll('[data-contact-step]')) {
      /** @type {HTMLElement} */ (block).hidden = !steps.includes(block.dataset.contactStep);
    }
  }

  #populateErrorMessage() {
    const errorText = this.querySelector('[data-error-text]');

    if (errorText && this.dataset.errorMessage) {
      errorText.textContent = this.dataset.errorMessage;
    }
  }

  /**
   * Show a specific form state. Called from theme-editor.js on block:select.
   * @param {string} step - 'form', 'error', or 'success'
   */
  showStep(step) {
    if (step === 'error') {
      this.#showSteps(['error', 'form']);
    } else {
      this.#showOnly(step);
    }
  }

  /**
   * Restore the server-rendered state. Called from theme-editor.js on block:deselect.
   */
  resetToDefault() {
    for (const [block, wasHidden] of this.#initialStates) {
      block.hidden = wasHidden;
    }
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
